import { logger } from '@common/logging/logger';

/*
 * Partial floorplan generation queue, shared by all FddmServiceImpl instances (the factory makes a
 * new one per call). A newer generateFloorplan request aborts a running one, which would drop the
 * floorplan of the areas that request was for (e.g. undoing items and templates in one step).
 * Instead, item ids requested while a request runs are merged into one follow-up request.
 */
const pendingItemIds = new Set<string>();
let runningGeneration: Promise<void> | null = null;

const generatePending = async (generate: (itemIds: string[]) => Promise<void>): Promise<void> => {
  try {
    while (pendingItemIds.size > 0) {
      const itemIds = [...pendingItemIds];
      pendingItemIds.clear();

      try {
        await generate(itemIds);
      } catch (error: unknown) {
        logger.error('Failed to generate the partial floorplan.', error);
      }
    }
  } finally {
    // Right when the queue is empty: ids queued after this start a new run
    runningGeneration = null;
  }
};

/**
 * @description Queues the item ids for partial floorplan generation. Resolves once their floorplan
 * (and that of any ids queued after them) is applied.
 */
export const enqueuePartialFloorplanGeneration = (
  itemIds: string[],
  generate: (itemIds: string[]) => Promise<void>,
): Promise<void> => {
  if (itemIds.length === 0) return runningGeneration ?? Promise.resolve();

  itemIds.forEach((id) => pendingItemIds.add(id));
  runningGeneration ??= generatePending(generate);

  return runningGeneration;
};
